import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Ticket } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, StatusChip, fmtDate } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { code: "", discount_type: "percent", value: 10, min_order_value: 0, max_discount: "", usage_limit: "", is_active: true };

export default function Coupons() {
  const { data, loading, error, refetch } = useFetch("/admin/coupons", []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.code.trim()) return toast.error("Code required");
    setBusy(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code.trim(), discount_type: form.discount_type, value: Number(form.value),
        min_order_value: Number(form.min_order_value) || 0,
        max_discount: form.max_discount === "" ? null : Number(form.max_discount),
        usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit), is_active: true,
      });
      toast.success("Coupon created"); setOpen(false); setForm(EMPTY); refetch();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const remove = async (c) => { try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Coupon deactivated"); refetch(); } catch (e) { toast.error(fmtErr(e)); } };
  const review = async (c, action) => {
    try { await api.post(`/admin/coupons/${c.id}/review`, { action }); toast.success(action === "approve" ? "Coupon approved and live" : "Coupon rejected"); refetch(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const chip = (c) => c.approval_status === "PENDING_APPROVAL" ? ["PENDING APPROVAL", "amber"] : c.approval_status === "REJECTED" ? ["REJECTED", "red"] : c.is_active ? ["ACTIVE", "green"] : ["INACTIVE", "gray"];

  return (
    <div>
      <PageHeader title="Coupons" subtitle="Create and manage promotional coupons" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-coupon-btn"><Plus className="h-4 w-4 mr-1.5" /> New Coupon</Button></DialogTrigger>
          <DialogContent data-testid="coupon-dialog">
            <DialogHeader><DialogTitle>New Coupon</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Code</Label><Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="SONU50" className="mt-1.5" data-testid="coupon-code-input" /></div>
              <div><Label>Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => set("discount_type", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Value</Label><Input type="number" value={form.value} onChange={(e) => set("value", e.target.value)} className="mt-1.5" data-testid="coupon-value-input" /></div>
              <div><Label>Min order ₹</Label><Input type="number" value={form.min_order_value} onChange={(e) => set("min_order_value", e.target.value)} className="mt-1.5" data-testid="coupon-min-input" /></div>
              <div><Label>Max discount ₹</Label><Input type="number" value={form.max_discount} onChange={(e) => set("max_discount", e.target.value)} placeholder="optional" className="mt-1.5" data-testid="coupon-max-input" /></div>
              <div className="col-span-2"><Label>Usage limit</Label><Input type="number" value={form.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} placeholder="optional" className="mt-1.5" data-testid="coupon-limit-input" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={busy} data-testid="coupon-save-btn">{busy ? "Saving…" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />

      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
        !data?.length ? <SectionCard className="p-2"><EmptyState label="No coupons yet" hint="Create your first promo coupon" /></SectionCard> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="coupons-grid">
            {[...data].sort((a, b) => (b.approval_status === "PENDING_APPROVAL") - (a.approval_status === "PENDING_APPROVAL")).map((c) => (
              <SectionCard key={c.id} className="p-4" data-testid={`coupon-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2"><Ticket className="h-4 w-4 text-primary" /><span className="font-mono font-bold">{c.code}</span></div>
                  <StatusChip status={chip(c)[0]} tone={chip(c)[1]} data-testid={`coupon-status-${c.id}`} />
                </div>
                <div className="mt-2 text-lg font-extrabold font-outfit">{c.discount_type === "flat" ? `₹${c.value} off` : `${c.value}% off`}</div>
                {c.title && <div className="text-sm font-medium mt-1" data-testid={`coupon-title-${c.id}`}>{c.title}</div>}
                <div className="text-xs text-muted-foreground mt-1" data-testid={`coupon-scope-${c.id}`}>{c.shop_id ? `Shop coupon${c.shop_name ? ` · ${c.shop_name}` : ""}` : "Platform-wide"}</div>
                <div className="text-xs text-muted-foreground mt-1">Min ₹{c.min_order_value || 0}{c.max_discount ? ` · Max ₹${c.max_discount}` : ""}</div>
                <div className="text-xs text-muted-foreground">Used {c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}{c.per_customer_limit ? ` · ${c.per_customer_limit} per customer` : ""}</div>
                {c.created_by_role === "shopkeeper" && <div className="text-xs mt-1">Created by shop{c.expires_at ? ` · ends ${fmtDate(c.expires_at)}` : ""}</div>}
                <div className="mt-3 pt-3 border-t flex justify-end gap-2">
                  {c.approval_status === "PENDING_APPROVAL" && <>
                    <Button size="sm" onClick={() => review(c, "approve")} data-testid={`coupon-approve-${c.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => review(c, "reject")} data-testid={`coupon-reject-${c.id}`}>Reject</Button></>}
                  <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`coupon-delete-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </SectionCard>
            ))}
          </div>
        )}
    </div>
  );
}
